import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { 
  ArrowLeft, 
  BookOpen,
  Church,
  Heart,
  Users,
  CheckCircle,
  MessageCircle,
  GraduationCap,
  Sparkles,
  ExternalLink,
  Play
} from 'lucide-react';
import { AutoTranslate } from '../hooks/useAutoTranslate';

const ConvertPage = () => {
  const { t, isRTL } = useLanguage();

  const steps = [
    {
      icon: MessageCircle,
      title: 'Ta kontakt',
      desc: 'Kontakta din närmaste katolska församling. Det är det viktigaste första steget. Tala gärna med någon katolik du känner om vad som är viktigt i hans eller hennes liv som katolik.'
    },
    {
      icon: Church,
      title: 'Delta i mässan',
      desc: 'För att komma in i katolskt gudstjänstliv och lära känna det som är centrum i katolikernas tro är det mycket bra att delta i församlingens mässa regelbundet.'
    },
    {
      icon: GraduationCap,
      title: 'Undervisning',
      desc: 'Du går i undervisning och har enskilda möten med en präst eller församlingssyster där du får tillfälle att ställa frågor och få andlig vägledning. Räkna med minst ett år.'
    },
    {
      icon: Sparkles,
      title: 'Upptagning i kyrkan',
      desc: 'Vid Påskvakan eller ett annat tillfälle tas du upp i Kyrkan. Om du redan är döpt konverterar du. Om du är odöpt döps du, konfirmeras och tar emot din första kommunion.'
    },
  ];

  const reasons = [
    {
      title: 'Apostolisk tradition',
      desc: 'Den katolska kyrkan kan spåra sin historia tillbaka till apostlarna och Jesus Kristus själv.'
    },
    {
      title: 'Eukaristin',
      desc: 'I mässan tar vi emot Kristi verkliga kropp och blod i eukaristin.'
    },
    {
      title: 'Gemenskapen',
      desc: 'Bli del av en världsvid gemenskap med över en miljard katoliker.'
    },
    {
      title: 'Sakramenten',
      desc: 'Genom sakramenten möter vi Guds nåd på ett konkret och påtagligt sätt.'
    },
  ];

  const resources = [
    { type: 'film', name: 'Sycamore', desc: 'Filmserie om katolsk tro', url: 'https://kpn.se/resurser/sycamore' },
    { type: 'film', name: 'Nikodemusmöten', desc: 'Filmserie för sökare', url: 'https://kpn.se/resurser/nikodemusmoten' },
  ];

  const books = [
    'Katolska kyrkans katekes',
    'Katolska kyrkans lilla katekes',
    'Youcat - Katolska kyrkans ungdomskatekes',
    'Vägen till Rom - Yvonne Maria Werner',
    'Navigera med Gud - Michel Remery',
    'Oremus - Svensk katolsk bönbok',
    'Rakt på sak om att vara katolik - Gallagher/Henesy',
    'Katolska kyrkan i fickformat - Erwin Bischofberger',
    'Trosmeditation - Anders Arborelius',
    'Katolskt ABC - Per Beskow',
  ];

  return (
    <div className="min-h-screen themed-page">
      <Navbar />

      {/* Header */}
      <section className="themed-hero relative h-96 overflow-hidden bg-gradient-to-r from-blue-700 via-indigo-700 to-blue-800">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='52' height='26' viewBox='0 0 52 26' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M10 10c0-2.21-1.79-4-4-4-3.314 0-6-2.686-6-6h2c0 2.21 1.79 4 4 4 3.314 0 6 2.686 6 6 0 2.21 1.79 4 4 4 3.314 0 6 2.686 6 6 0 2.21 1.79 4 4 4v2c-3.314 0-6-2.686-6-6 0-2.21-1.79-4-4-4-3.314 0-6-2.686-6-6zm25.464-1.95l8.486 8.486-1.414 1.414-8.486-8.486 1.414-1.414z' /%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <Link to="/fordjupning" className={`inline-flex items-center text-blue-200 hover:text-white mb-4 transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}>
              <ArrowLeft className={`w-4 h-4 ${isRTL ? 'ml-2 rotate-180' : 'mr-2'}`} />
              <AutoTranslate>Tillbaka till Fördjupning</AutoTranslate>
            </Link>
            <h1 className="responsive-hero-title font-serif font-bold text-white mb-4 fade-in">
              <AutoTranslate>Vill du bli katolik?</AutoTranslate>
            </h1>
            <p className="text-xl text-blue-100 fade-in max-w-2xl">
              <AutoTranslate>I så fall är du inte ensam. Omkring hundra personer om året i Sverige blir upptagna i den katolska kyrkan. Vad du först och främst bör göra är att ta kontakt med närmaste katolska församling.</AutoTranslate>
            </p>
          </div>
        </div>
      </section>

      {/* Why Catholic */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-serif font-bold text-gray-900 mb-4">
              <AutoTranslate>Varför katolska kyrkan?</AutoTranslate>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              <AutoTranslate>Den katolska kyrkan erbjuder en djup och rik trostradition som har format kristendomen i över 2000 år.</AutoTranslate>
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {reasons.map((reason, idx) => (
              <Card key={`reason-${reason.title}`} className="border-0 shadow-xl card-hover hover:shadow-xl transition-shadow">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    <AutoTranslate>{reason.title}</AutoTranslate>
                  </h3>
                  <p className="text-sm text-gray-600">
                    <AutoTranslate>{reason.desc}</AutoTranslate>
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Paths to Catholicism */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-serif font-bold text-gray-900 mb-4">
              <AutoTranslate>Två vägar in i kyrkan</AutoTranslate>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="border-0 shadow-xl card-hover">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-4">
                  <Heart className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  <AutoTranslate>Konversion</AutoTranslate>
                </h3>
                <p className="text-gray-600 mb-4">
                  <AutoTranslate>För dig som redan är döpt i ett annat kristet samfund. Alla dop gjorda i &quot;Faderns, Sonens och den helige Andes namn&quot; ser katolska kyrkan som giltiga. Du behöver inte döpas på nytt.</AutoTranslate>
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-xl card-hover">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mb-4">
                  <Sparkles className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  <AutoTranslate>Vuxendop</AutoTranslate>
                </h3>
                <p className="text-gray-600 mb-4">
                  <AutoTranslate>För dig som inte är döpt. Varje år döps ca 30-40 vuxna in i katolska kyrkan i Sverige. Vägen in följer ordningen &quot;Vuxenkatekumenatet&quot; och dopet sker normalt på påsknatten.</AutoTranslate>
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Steps to Convert */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-serif font-bold text-gray-900 mb-4">
              <AutoTranslate>Vägen till katolska kyrkan</AutoTranslate>
            </h2>
            <p className="text-gray-600">
              <AutoTranslate>Processen att bli katolik är en resa av tro, lärande och gemenskap. Eftersom alla människor är olika kan man inte i förväg säga exakt hur lång tid det tar.</AutoTranslate>
            </p>
          </div>

          <div className="space-y-6">
            {steps.map((step, idx) => {
              const Icon = step.icon;
              return (
                <div key={`step-${step.title}`} className={`flex gap-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-lg shadow-xl card-hover">
                      {idx + 1}
                    </div>
                  </div>
                  <Card className="flex-1 border-0 shadow-xl card-hover">
                    <CardContent className="p-6">
                      <div className={`flex items-center gap-3 mb-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <Icon className="w-5 h-5 text-blue-600" />
                        <h3 className="text-lg font-semibold text-gray-900">
                          <AutoTranslate>{step.title}</AutoTranslate>
                        </h3>
                      </div>
                      <p className="text-gray-600">
                        <AutoTranslate>{step.desc}</AutoTranslate>
                      </p>
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Resources */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-serif font-bold text-gray-900 mb-4">
              <AutoTranslate>Resurser för sökare</AutoTranslate>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {resources.map((resource, idx) => (
              <a 
                key={`resource-${resource.name || resource.url || idx}`}
                href={resource.url}
                target="_blank"
                rel="noopener noreferrer"
                className="block group"
              >
                <Card className="border-0 shadow-xl card-hover hover:shadow-xl transition-all h-full">
                  <CardContent className={`p-6 flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 group-hover:bg-red-200 transition-colors">
                      <Play className="w-6 h-6 text-red-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">{resource.name}</h3>
                      <p className="text-sm text-gray-600">
                        <AutoTranslate>{resource.desc}</AutoTranslate>
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </a>
            ))}
          </div>

          {/* Recommended books */}
          <Card className="border-0 shadow-xl card-hover">
            <CardContent className="p-6">
              <h3 className={`text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <BookOpen className="w-5 h-5 text-blue-600" />
                <AutoTranslate>Rekommenderade böcker</AutoTranslate>
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {books.map((book, idx) => (
                  <div key={`book-${book}-${idx}`} className={`flex items-center gap-2 text-sm text-gray-700 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <span className="text-blue-500">•</span>
                    {book}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* More Info Link */}
      <section className="py-8 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <a 
            href="https://www.katolskakyrkan.se/forsamlingsliv/vill-du-bli-katolik"
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center text-blue-600 hover:text-blue-800 font-medium ${isRTL ? 'flex-row-reverse' : ''}`}
          >
            <ExternalLink className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
            <AutoTranslate>Läs mer på katolskakyrkan.se</AutoTranslate>
          </a>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-0 shadow-2xl bg-gradient-to-r from-blue-600 to-indigo-700 text-white overflow-hidden">
            <CardContent className="p-8 md:p-12">
              <div className={`flex flex-col md:flex-row items-center gap-8 ${isRTL ? 'md:flex-row-reverse' : ''}`}>
                <div className="flex-shrink-0">
                  <div className="w-24 h-24 rounded-full bg-white/20 flex items-center justify-center">
                    <Users className="w-12 h-12" />
                  </div>
                </div>
                <div className={`flex-1 text-center ${isRTL ? 'md:text-right' : 'md:text-left'}`}>
                  <h3 className="text-2xl md:text-3xl font-serif font-bold mb-3">
                    <AutoTranslate>Ta första steget</AutoTranslate>
                  </h3>
                  <p className="text-blue-100 mb-6">
                    <AutoTranslate>Kontakta vår församling för att prata med en präst eller kateket om din resa mot den katolska tron. Vi välkomnar dig varmt!</AutoTranslate>
                  </p>
                  <div className={`flex flex-col sm:flex-row gap-4 justify-center ${isRTL ? 'md:justify-end' : 'md:justify-start'}`}>
                    <Link 
                      to="/contact"
                      className={`inline-flex items-center justify-center px-6 py-3 bg-white text-blue-700 font-semibold rounded-lg hover:bg-blue-50 transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}
                    >
                      <MessageCircle className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                      <AutoTranslate>Kontakta oss</AutoTranslate>
                    </Link>
                    <Link 
                      to="/churches"
                      className={`inline-flex items-center justify-center px-6 py-3 bg-white/10 text-white font-semibold rounded-lg hover:bg-white/20 transition-colors border border-white/30 ${isRTL ? 'flex-row-reverse' : ''}`}
                    >
                      <Church className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                      <AutoTranslate>Hitta en kyrka</AutoTranslate>
                    </Link>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ConvertPage;
